/**
 * Shared protocol export normalization — used by Word export and PDF block pipeline
 * so both paths parse the same heading hierarchy and skip duplicate section titles.
 */

import { parseProtocolHeadingLine } from './protocolHeadingLineUtils';

const STOP_WORDS = new Set([
  'OF', 'AND', 'THE', 'TO', 'IN', 'FOR', 'ON', 'WITH', 'OR', 'BY', 'AT', 'FROM', 'AN', 'A', 'AS', 'ASSOCIATED',
]);

function normalizeTitle(s) {
  return String(s || '')
    .toUpperCase()
    .replace(/[^A-Z0-9.\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractNumberAndText(s) {
  const raw = String(s || '').trim();
  const m = raw.match(/^(\d+(?:\.\d+)*)\.?\s*(.*)$/);
  if (!m) return { num: null, rest: normalizeTitle(raw) };
  return { num: m[1], rest: normalizeTitle(m[2] || '') };
}

function mainSectionNumber(s) {
  const m = String(s || '').match(/^(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

function keywords(str) {
  return normalizeTitle(str)
    .split(' ')
    .filter((w) => w && !STOP_WORDS.has(w) && !/^[0-9.]+$/.test(w));
}

function overlapScore(a, b) {
  const A = new Set(keywords(a));
  const B = new Set(keywords(b));
  if (A.size === 0 || B.size === 0) return 0;
  let inter = 0;
  A.forEach((w) => {
    if (B.has(w)) inter += 1;
  });
  return inter / Math.min(A.size, B.size);
}

/**
 * True when a heading line repeats the parent section title (already emitted as CHeading1).
 */
export function isDuplicateSectionHeadingText(text, sectionTitle) {
  const t = String(text || '').trim();
  const section = String(sectionTitle || '').trim();
  if (!t || !section) return false;

  const { num: secNum, rest: secRestNorm } = extractNumberAndText(section);
  const secMain = mainSectionNumber(section);

  const { num: blkNum, rest: blkRestNorm } = extractNumberAndText(t);
  const exactNorm = normalizeTitle(t) === normalizeTitle(section);
  const numericOnlyMatch =
    /^\d+(?:\.\d+)*\.?$/.test(t) && secNum && t.replace(/\.$/, '') === secNum;
  const numberOk = (!!blkNum && !!secNum && blkNum === secNum) || !blkNum;
  const restOk = blkRestNorm === '' || blkRestNorm === secRestNorm;
  const blkMain = mainSectionNumber(t);
  const highOverlap = blkMain === secMain && overlapScore(t, section) >= 0.5;
  const isSubsection = !!blkNum && /\./.test(blkNum) && !/^\d+(?:\.0+)?$/.test(blkNum);
  if (isSubsection) return false;

  return exactNorm || numericOnlyMatch || (numberOk && restOk) || highOverlap;
}

/**
 * Remove leading blocks that duplicate the section title (matches PDF export).
 */
export function stripLeadingDuplicateHeadingBlocks(blocks, sectionTitle) {
  if (!Array.isArray(blocks) || blocks.length === 0) return blocks;
  let cut = 0;
  for (let k = 0; k < Math.min(5, blocks.length); k += 1) {
    const blk = blocks[k];
    if (blk.type !== 'heading' && blk.type !== 'paragraph') break;
    const text =
      blk.type === 'heading'
        ? blk.text || ''
        : (blk.runs || []).map((r) => r.text).join('');
    if (isDuplicateSectionHeadingText(text, sectionTitle)) {
      cut += 1;
      continue;
    }
    break;
  }
  return cut > 0 ? blocks.slice(cut) : blocks;
}

/**
 * Word TOC outline level from protocol numbering (not HTML tag depth).
 * Returns null when the line should not appear in the TOC.
 */
export function outlineLevelForProtocolHeadingText(text, sectionRootPrefix = null) {
  const parsed = parseProtocolHeadingLine(text, { sectionRootPrefix });
  if (!parsed) return null;

  if (parsed.kind === 'main') {
    const secMain = sectionRootPrefix
      ? parseInt(String(sectionRootPrefix).split('.')[0], 10)
      : null;
    const lineMain = parseInt(parsed.number, 10);
    // Promote the next sequential main section embedded in prior section content (e.g. 13 after 12).
    if (secMain != null && lineMain !== secMain) {
      return lineMain === secMain + 1 ? 0 : null;
    }
    return 0;
  }

  const dotCount = (parsed.number.match(/\./g) || []).length;
  return Math.min(dotCount, 4);
}

export function protocolHeadingStyleForOutlineLevel(outlineLevel) {
  const styles = ['CHeading1', 'CHeading2', 'CHeading3', 'CHeading4', 'CHeading5'];
  return styles[Math.min(Math.max(outlineLevel, 0), styles.length - 1)];
}

/** Outline depth from a section title number (e.g. "1.2 TRIAL SCHEMA" → 1, "3." → 0). */
export function outlineLevelFromSectionTitle(title) {
  const m = String(title || '').match(/^(\d+(?:\.\d+)*)/);
  if (!m) return 0;
  const dotCount = (m[1].match(/\./g) || []).length;
  return Math.min(dotCount, 4);
}

export function isMainProtocolSectionTitle(title) {
  return outlineLevelFromSectionTitle(title) === 0;
}

/** Section 1 subsections backed by image/SOA data rather than LLM output. */
export const M11_SECTION_1_SUBSECTION_TITLES = [
  '1.2 TRIAL SCHEMA',
  '1.3 SCHEDULE OF ACTIVITIES',
];

/** M11 appendix sections that must appear in export even when not yet generated. */
export const M11_APPENDIX_SECTION_TITLES = [
  '13. APPENDIX: GLOSSARY OF TERMS AND ABBREVIATIONS',
  '14. APPENDIX: REFERENCES',
];

export function sectionNumberPrefixFromTitle(title) {
  return String(title || '').trim().match(/^(\d+(?:\.\d+)*)/)?.[1] || null;
}

function compareSectionNumberPrefixes(a, b) {
  const aParts = String(a || '').split('.').map((part) => parseInt(part, 10) || 0);
  const bParts = String(b || '').split('.').map((part) => parseInt(part, 10) || 0);
  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i += 1) {
    const diff = (aParts[i] ?? 0) - (bParts[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

export function isM11Section1SpecialTitle(title) {
  const prefix = sectionNumberPrefixFromTitle(title);
  return prefix === '1.2' || prefix === '1.3';
}

export function appendMissingM11Section1Subsections(sections) {
  const list = Array.isArray(sections) ? [...sections] : [];

  for (const title of M11_SECTION_1_SUBSECTION_TITLES) {
    const requiredPrefix = sectionNumberPrefixFromTitle(title);
    const exists = list.some(
      (section) => sectionNumberPrefixFromTitle(section?.title) === requiredPrefix,
    );
    if (exists) continue;

    const stub = { title, output: '', context: '', prompt: '' };
    const insertIndex = list.findIndex((section) => {
      const prefix = sectionNumberPrefixFromTitle(section?.title);
      return prefix && requiredPrefix && compareSectionNumberPrefixes(prefix, requiredPrefix) > 0;
    });
    if (insertIndex >= 0) {
      list.splice(insertIndex, 0, stub);
    } else {
      list.push(stub);
    }
  }

  return list;
}

export function appendMissingM11AppendixSections(sections) {
  const mapped = Array.isArray(sections)
    ? sections.map((section) => {
        const canonicalTitle = canonicalizeM11AppendixSectionTitle(section?.title);
        return canonicalTitle !== section?.title
          ? { ...section, title: canonicalTitle }
          : section;
      })
    : [];

  // Collapse pre-existing duplicate appendix sections (e.g. from a race
  // between concurrent section auto-creation calls) down to one entry per
  // section number, keeping whichever copy already has content.
  const list = [];
  const appendixIndexByNum = new Map();
  for (const section of mapped) {
    const mainNum = String(section?.title || '').trim().match(/^(\d+)/)?.[1];
    const isAppendix = mainNum && CANONICAL_M11_APPENDIX_TITLES_BY_NUM[mainNum];
    if (isAppendix && appendixIndexByNum.has(mainNum)) {
      const existingIdx = appendixIndexByNum.get(mainNum);
      const existingHasOutput = String(list[existingIdx]?.output || '').trim();
      const candidateHasOutput = String(section?.output || '').trim();
      if (!existingHasOutput && candidateHasOutput) {
        list[existingIdx] = section;
      }
      continue;
    }
    if (isAppendix) appendixIndexByNum.set(mainNum, list.length);
    list.push(section);
  }

  for (const title of M11_APPENDIX_SECTION_TITLES) {
    const mainNum = title.match(/^(\d+)/)?.[1];
    const exists = list.some((s) => {
      const m = String(s?.title || '').trim().match(/^(\d+)/);
      return m && m[1] === mainNum;
    });
    if (!exists) {
      list.push({ title, output: '', context: '', prompt: '' });
    }
  }
  return list;
}

export const CANONICAL_M11_APPENDIX_TITLES_BY_NUM = Object.freeze({
  13: '13. APPENDIX: GLOSSARY OF TERMS AND ABBREVIATIONS',
  14: '14. APPENDIX: REFERENCES',
});

export function canonicalizeM11AppendixSectionTitle(title) {
  const mainNum = String(title || '').trim().match(/^(\d+)/)?.[1];
  return mainNum && CANONICAL_M11_APPENDIX_TITLES_BY_NUM[mainNum]
    ? CANONICAL_M11_APPENDIX_TITLES_BY_NUM[mainNum]
    : title;
}

export function resolveProtocolSectionTitle(title) {
  return canonicalizeM11AppendixSectionTitle(title);
}

function findEmbeddedMainSectionSplit(content, sectionNum) {
  const text = String(content || '');
  if (!text.trim()) return null;

  const patterns = [
    new RegExp(`(?:^|[\\n\\r])\\s*Section\\s+${sectionNum}\\s*[:.\\-–—]\\s*`, 'im'),
    new RegExp(`(?:^|[\\n\\r])\\s*${sectionNum}\\.\\s*APPENDIX`, 'im'),
    new RegExp(`(?:^|[\\n\\r])\\s*${sectionNum}\\s+APPENDIX`, 'im'),
    new RegExp(`(?:^|[\\n\\r])\\s*${sectionNum}\\.\\s*APPENDIX\\s*:\\s*REFERENCES`, 'im'),
    new RegExp(`(?:^|[\\n\\r])\\s*${sectionNum}\\s+APPENDIX\\s+REFERENCES`, 'im'),
    new RegExp(`(?:^|[\\n\\r])\\s*\\d+\\s+Section\\s+${sectionNum}\\s*[:.]`, 'im'),
  ];

  let splitIndex = -1;
  for (const pattern of patterns) {
    const match = pattern.exec(text);
    if (match && (splitIndex < 0 || match.index < splitIndex)) {
      splitIndex = match.index;
    }
  }

  if (splitIndex < 0) return null;
  return {
    before: text.slice(0, splitIndex).trimEnd(),
    after: text.slice(splitIndex).trimStart(),
  };
}

function assignSectionOutputIfEmpty(list, sectionNum, content) {
  const body = String(content || '').trim();
  if (!body) return;

  const idx = list.findIndex((section) => {
    const match = String(section?.title || '').trim().match(/^(\d+)/);
    return match && parseInt(match[1], 10) === sectionNum;
  });

  if (idx >= 0) {
    const existing = String(list[idx].output || '').trim();
    if (!existing) {
      list[idx] = { ...list[idx], output: body };
    }
    return;
  }

  const title = CANONICAL_M11_APPENDIX_TITLES_BY_NUM[sectionNum];
  if (title) {
    list.push({ title, output: body, context: '', prompt: '' });
  }
}

/**
 * Ensures M11 appendix sections 13–14 exist as separate cards and pulls any
 * glossary/reference content mistakenly embedded inside section 12 output.
 */
export function isM11AppendixSectionTitle(title) {
  return /^1[34]\.\s/i.test(String(title || '').trim());
}

export function getProtocolSectionsForExport(sections) {
  return normalizeProtocolSectionsList(sections);
}

export function normalizeProtocolSectionsList(sections) {
  const list = appendMissingM11Section1Subsections(
    appendMissingM11AppendixSections(sections),
  ).map((section) => ({ ...section }));

  const section12Index = list.findIndex((section) =>
    /^12\./i.test(String(section?.title || '').trim()),
  );
  if (section12Index < 0) return list;

  let section12Output = String(list[section12Index].output || '');

  const split14In12 = findEmbeddedMainSectionSplit(section12Output, 14);
  if (split14In12) {
    section12Output = split14In12.before;
    assignSectionOutputIfEmpty(list, 14, split14In12.after);
  }

  const split13In12 = findEmbeddedMainSectionSplit(section12Output, 13);
  if (split13In12) {
    list[section12Index] = { ...list[section12Index], output: split13In12.before };
    let section13Output = split13In12.after;

    const split14In13 = findEmbeddedMainSectionSplit(section13Output, 14);
    if (split14In13) {
      assignSectionOutputIfEmpty(list, 14, split14In13.after);
      section13Output = split14In13.before;
    }

    assignSectionOutputIfEmpty(list, 13, section13Output);
  } else if (split14In12) {
    list[section12Index] = { ...list[section12Index], output: section12Output };
  }

  return list;
}

/**
 * Merges section numbers split across lines or soft wraps.
 * Only merges when the result validates as a protocol subsection heading.
 */
export function mergeFragmentedSectionNumbers(content) {
  if (!content) return content;
  let s = String(content);

  const tryMerge = (prefix, rest) => {
    const candidate = `${prefix}${rest.trimStart()}`.trim();
    const parsed = parseProtocolHeadingLine(candidate);
    return parsed && parsed.kind === 'subsection' ? candidate : null;
  };

  s = s.replace(/(\d+(?:\.\d+)*\.)\s+(\d+\s+[^\n]+)/g, (match, prefix, rest) => {
    return tryMerge(prefix, rest) || match;
  });
  s = s.replace(/(\d+(?:\.\d+)*\.)\n+(\d+\s+[^\n]+)/g, (match, prefix, rest) => {
    return tryMerge(prefix, rest) || match;
  });
  s = s.replace(/(\d+)\n+(\.\d+\s+[^\n]+)/g, (match, prefix, rest) => {
    return tryMerge(prefix, rest) || match;
  });
  s = s.replace(/(\d+\.\d+(?:\.\d+)*)\n+(\.\d+\s+[^\n]+)/g, (match, prefix, rest) => {
    return tryMerge(prefix, rest) || match;
  });

  return s;
}

function sectionRootPrefixFromTitle(sectionTitle) {
  return (String(sectionTitle || '').match(/^(\d+(?:\.\d+)*)/) || [])[1] || null;
}

const HANGING_HEADING_ENDING = /\b(of|and|or|for|to|from|the|a|an|in|on|with|by)$/i;

/**
 * Split "10.4.1 Primary Objective: To evaluate..." into heading + body remainder.
 */
export function splitColonObjectiveHeadingLine(line) {
  const trimmed = String(line ?? '').trim();
  if (!trimmed) return null;

  const objective = trimmed.match(
    /^(\d+(?:\.\d+)+)\s+((?:Primary|Secondary|Exploratory)\s+Objective)\s*:\s*(.+)$/i,
  );
  if (objective) {
    return {
      headingLine: `${objective[1]} ${objective[2]}`.trim(),
      remainder: objective[3].trim(),
    };
  }

  const generic = trimmed.match(/^(\d+(?:\.\d+)+)\s+([A-Z][^:]{2,60}?)\s*:\s+(.+)$/);
  if (generic) {
    const label = generic[2].trim();
    const body = generic[3].trim();
    if (
      label.split(/\s+/).length <= 10 &&
      !/\b(to evaluate|to assess|to determine|will|shall)\b/i.test(label) &&
      /\b(to evaluate|to assess|to determine|to compare|will|shall)\b/i.test(body)
    ) {
      return { headingLine: `${generic[1]} ${label}`, remainder: body };
    }
  }

  return null;
}

function applyColonHeadingSplitsToLines(lines) {
  const out = [];
  for (const line of lines) {
    const split = splitColonObjectiveHeadingLine(line);
    if (split && parseProtocolHeadingLine(split.headingLine)) {
      out.push(split.headingLine);
      if (split.remainder) out.push(split.remainder);
      continue;
    }
    out.push(line);
  }
  return out;
}

/**
 * True when a numbered line is an incomplete heading wrapped mid-phrase (e.g. "7.1 Discontinuation of").
 */
export function isPartialProtocolHeadingLine(line, sectionRootPrefix = null) {
  const trimmed = String(line ?? '').trim();
  if (!/^\d+(?:\.\d+)*\s+\S/.test(trimmed)) return false;

  if (splitColonObjectiveHeadingLine(trimmed)) return false;
  if (/:\s*\S/.test(trimmed) && /\b(to evaluate|to assess|to determine|to compare)\b/i.test(trimmed)) {
    return false;
  }

  if (HANGING_HEADING_ENDING.test(trimmed)) return true;

  const parsed = parseProtocolHeadingLine(trimmed, { sectionRootPrefix });
  if (!parsed) return false;
  return HANGING_HEADING_ENDING.test(parsed.title);
}

/** Title-case continuation after a partial subsection line (e.g. "7.1 Discontinuation of"). */
export function isSubsectionHeadingContinuationLine(currentLine, nextLine) {
  const cur = String(currentLine ?? '').trim();
  const next = String(nextLine ?? '').trim();
  if (!next) return false;
  if (/^\d+(?:\.\d+)*\s/.test(next)) return false;
  if (/^[a-z(]/.test(next)) return false;
  if (next.length > 160) return false;
  if (/^(All|The|This|These|Each|In |For |During |After |Before )/i.test(next)) return false;

  if (/^\d+(?:\.\d+)+\.?\s*$/.test(cur)) return true;

  if (!/^\d+(?:\.\d+)+\s+/.test(cur)) return false;
  return isPartialProtocolHeadingLine(cur);
}

export function isMainSectionTitleContinuationLine(currentLine, nextLine) {
  const cur = String(currentLine ?? '').trim();
  const next = String(nextLine ?? '').trim();
  if (!/^\d+\.?\s+[A-Z]/.test(cur)) return false;
  if (!next || /^\d+(?:\.\d+)*\s/.test(next)) return false;
  return /^[A-Z][A-Z0-9\s&,\-–—/()]+$/.test(next) && next.length > 3;
}

/**
 * Join headings split across soft wraps: "7.1 Discontinuation of" + "Trial Intervention".
 */
export function joinFragmentedHeadingContinuationLines(content, sectionRootPrefix = null) {
  if (!content) return content;
  const lines = String(content).replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let i = 0;
  while (i < lines.length) {
    let merged = lines[i];
    while (i + 1 < lines.length) {
      const cur = merged.trim();
      const next = lines[i + 1].trim();
      if (!cur || !next) break;

      const curNum = cur.match(/^(\d+(?:\.\d+)*)/)?.[1];
      const nextNum = next.match(/^(\d+(?:\.\d+)*)/)?.[1];
      if (nextNum && curNum && nextNum !== curNum) break;

      // "1.1." or "3.1" on one line + title on the next
      if (/^\d+(?:\.\d+)+\.?\s*$/.test(cur) && next && !/^\d/.test(next)) {
        i += 1;
        merged = `${cur.replace(/\.\s*$/, '')} ${next}`;
        continue;
      }

      if (
        isPartialProtocolHeadingLine(cur, sectionRootPrefix) ||
        isSubsectionHeadingContinuationLine(cur, next) ||
        isMainSectionTitleContinuationLine(cur, next)
      ) {
        i += 1;
        merged = `${cur} ${next}`;
        continue;
      }
      break;
    }
    out.push(merged);
    i += 1;
  }
  return applyColonHeadingSplitsToLines(out).join('\n');
}

/**
 * Join split heading HTML: </p><p> between "7.1 Discontinuation of" and "Trial Intervention".
 */
export function joinFragmentedHeadingContinuationInHtml(html, sectionRootPrefix = null) {
  if (!html || !/<\w+/i.test(html)) return html;
  let result = String(html);
  result = result.replace(
    /<(?:p|div)[^>]*>\s*(\d+(?:\.\d+)+\.?)\s*<\/(?:p|div)>\s*<(?:p|div)[^>]*>([^<]+)<\/(?:p|div)>/gi,
    (match, num, title) => {
      const candidate = `${String(num).replace(/\.$/, '')} ${title.trim()}`.replace(/\s+/g, ' ');
      if (!parseProtocolHeadingLine(candidate, { sectionRootPrefix })) return match;
      return `<p>${candidate}</p>`;
    },
  );
  result = result.replace(
    /<p[^>]*>([^<]*\d+(?:\.\d+)+\s+[^<]*?\b(?:of|and|or|for|to|from|the|a|an|in|on|with|by)\s*)<\/p>\s*<p[^>]*>([^<]+)<\/p>/gi,
    (match, head, tail) => {
      const candidate = `${head.trim()} ${tail.trim()}`.replace(/\s+/g, ' ');
      if (
        !isPartialProtocolHeadingLine(candidate, sectionRootPrefix) &&
        parseProtocolHeadingLine(candidate, { sectionRootPrefix })
      ) {
        return `<p>${candidate}</p>`;
      }
      return match;
    },
  );
  result = result.replace(
    /<(?:p|div)[^>]*>([^<]*\d+(?:\.\d+)+\s+[^<]*?)<\/(?:p|div)>\s*<(?:p|div)[^>]*>([A-Z][A-Za-z0-9\s&,\-–—/():]+)<\/(?:p|div)>/gi,
    (match, head, tail) => {
      const headTrim = head.trim();
      if (!isPartialProtocolHeadingLine(headTrim, sectionRootPrefix) && !/^\d+(?:\.\d+)+\.?\s*$/.test(headTrim)) {
        return match;
      }
      const candidate = `${headTrim} ${tail.trim()}`.replace(/\s+/g, ' ');
      if (
        !isPartialProtocolHeadingLine(candidate, sectionRootPrefix) &&
        parseProtocolHeadingLine(candidate, { sectionRootPrefix })
      ) {
        return `<p>${candidate}</p>`;
      }
      return match;
    },
  );
  return result;
}

function headingTagForSubsectionNumber(number) {
  const dotCount = (String(number).match(/\./g) || []).length;
  const level = Math.min(dotCount + 1, 6);
  return `h${level}`;
}

/** Find a protocol subsection heading embedded in flowing paragraph text. */
export function findEmbeddedSubsectionHeading(plain, sectionRootPrefix = null) {
  const text = String(plain || '').replace(/\s+/g, ' ').trim();
  if (!text) return null;

  const re = /(?:^|[.!?]\s+)(\d+(?:\.\d+)+\s+)/g;
  let match;
  while ((match = re.exec(text)) !== null) {
    const numInMatch = match[0].match(/(\d+(?:\.\d+)+\s+)/);
    if (!numInMatch) continue;
    const numberPart = numInMatch[1].trim();
    const numberIndex = match.index + match[0].indexOf(numInMatch[1]);
    const titleStart = numberIndex + numInMatch[1].length;
    const remainder = text.slice(titleStart).trim();
    if (!remainder) continue;
    const words = remainder.split(/\s+/);
    for (let len = Math.min(words.length, 14); len >= 1; len -= 1) {
      const title = words.slice(0, len).join(' ');
      const candidate = `${numberPart} ${title}`.replace(/\s+/g, ' ').trim();
      if (isPartialProtocolHeadingLine(candidate, sectionRootPrefix)) continue;
      if (parseProtocolHeadingLine(candidate, { sectionRootPrefix })) {
        return {
          before: text.slice(0, numberIndex).trim(),
          heading: candidate,
          after: words.slice(len).join(' ').trim(),
        };
      }
    }
  }

  const leading = text.match(/^(\d+(?:\.\d+)+\s+)(.+)$/);
  if (leading) {
    const colon = splitColonObjectiveHeadingLine(text);
    if (colon && parseProtocolHeadingLine(colon.headingLine, { sectionRootPrefix })) {
      return { before: '', heading: colon.headingLine, after: colon.remainder };
    }

    const words = leading[2].split(/\s+/);
    for (let len = Math.min(words.length, 14); len >= 1; len -= 1) {
      const candidate = `${leading[1].trim()} ${words.slice(0, len).join(' ')}`.replace(/\s+/g, ' ').trim();
      if (isPartialProtocolHeadingLine(candidate, sectionRootPrefix)) continue;
      if (parseProtocolHeadingLine(candidate, { sectionRootPrefix })) {
        return {
          before: '',
          heading: candidate,
          after: words.slice(len).join(' ').trim(),
        };
      }
    }
  }

  return null;
}

/**
 * Put each protocol subsection line on its own paragraph so marked / block parsers see it.
 */
export function normalizeProtocolSubsectionLineBreaks(content) {
  if (!content) return content;
  let s = String(content);
  // Single newline before subsection number → paragraph break
  s = s.replace(
    /([^\n])\n(\d+\.\d+(?:\.\d+)*\s+[A-Za-z])/g,
    '$1\n\n$2',
  );
  // Glued after sentence end: "...criteria. 3.1 Title"
  s = s.replace(
    /([.!?])\s+(\d+\.\d+(?:\.\d+)*\s+[A-Z][^\n]{2,120})/g,
    '$1\n\n$2',
  );
  return s;
}

/**
 * Convert plain / bold markdown subsection lines to ATX headings for marked.parse.
 */
export function promotePlainSubsectionLinesToMarkdown(md, sectionRootPrefix = null) {
  if (!md) return md;
  return String(md)
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('|') || trimmed.startsWith('#')) return line;

      const boldMatch = trimmed.match(/^(?:\*\*|__)(\d+(?:\.\d+)+)\s+(.+?)(?:\*\*|__)\s*$/);
      if (boldMatch) {
        const full = `${boldMatch[1]} ${boldMatch[2].trim()}`;
        if (parseProtocolHeadingLine(full, { sectionRootPrefix })) {
          const depth = (boldMatch[1].match(/\./g) || []).length;
          return `${'#'.repeat(Math.min(depth + 1, 4))} ${full}`;
        }
      }

      const plainMatch = trimmed.match(/^(\d+(?:\.\d+)+)\s+(.+)$/);
      if (plainMatch) {
        const colon = splitColonObjectiveHeadingLine(trimmed);
        if (colon && parseProtocolHeadingLine(colon.headingLine, { sectionRootPrefix })) {
          const num = colon.headingLine.match(/^(\d+(?:\.\d+)+)/)?.[1] || '';
          const depth = (num.match(/\./g) || []).length;
          return `${'#'.repeat(Math.min(depth + 1, 4))} ${colon.headingLine}\n\n${colon.remainder}`;
        }

        const full = `${plainMatch[1]} ${plainMatch[2].trim()}`;
        if (
          !isPartialProtocolHeadingLine(full, sectionRootPrefix) &&
          parseProtocolHeadingLine(full, { sectionRootPrefix })
        ) {
          const depth = (plainMatch[1].match(/\./g) || []).length;
          return `${'#'.repeat(Math.min(depth + 1, 4))} ${full}`;
        }
      }

      return line;
    })
    .join('\n');
}

/**
 * Promote <p><strong>3.1 Title</strong></p> (and similar) to real heading tags for TOC.
 */
export function promoteProtocolSubsectionHeadingsInHtml(html, sectionRootPrefix = null) {
  if (!html || !/<\w+/i.test(html)) return html;
  let result = String(html);

  const promoteBlock = (tag, inner) => {
    const text = inner.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    if (!text) return null;

    const colon = splitColonObjectiveHeadingLine(text);
    if (colon && parseProtocolHeadingLine(colon.headingLine, { sectionRootPrefix })) {
      const num = colon.headingLine.match(/^(\d+(?:\.\d+)+)/)?.[1];
      const htag = headingTagForSubsectionNumber(num);
      const body = colon.remainder ? `<p>${colon.remainder}</p>` : '';
      return `<${htag}>${colon.headingLine}</${htag}>${body}`;
    }

    if (isPartialProtocolHeadingLine(text, sectionRootPrefix)) return null;
    if (!parseProtocolHeadingLine(text, { sectionRootPrefix })) return null;
    const num = text.match(/^(\d+(?:\.\d+)+)/)?.[1];
    if (!num) return null;
    const htag = headingTagForSubsectionNumber(num);
    return `<${htag}>${text}</${htag}>`;
  };

  // <p>1.1.</p><p>Protocol Synopsis</p>
  result = result.replace(
    /<(?:p|div)[^>]*>\s*(\d+(?:\.\d+)+\.?)\s*<\/(?:p|div)>\s*<(?:p|div)[^>]*>([^<]+)<\/(?:p|div)>/gi,
    (match, num, title) => {
      const full = `${String(num).replace(/\.$/, '')} ${title.trim()}`.replace(/\s+/g, ' ');
      if (!parseProtocolHeadingLine(full, { sectionRootPrefix })) return match;
      const htag = headingTagForSubsectionNumber(full.match(/^(\d+(?:\.\d+)+)/)?.[1] || num);
      return `<${htag}>${full}</${htag}>`;
    },
  );

  // <p>intro. 3.1 Title body...</p> or <p>3.1 Title<br>body</p>
  result = result.replace(/<(?:p|div)[^>]*>([\s\S]*?)<\/(?:p|div)>/gi, (match, inner) => {
    if (/<(?:h[1-6]|table|ul|ol)\b/i.test(inner)) return match;
    const plain = inner
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!plain) return match;

    const brParts = inner.split(/<br\s*\/?>/i);
    if (brParts.length > 1) {
      const headText = brParts[0].replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
      const promoted = promoteBlock('p', headText);
      if (promoted) {
        const tail = brParts
          .slice(1)
          .map((p) => p.replace(/<[^>]+>/g, '').trim())
          .filter(Boolean)
          .join(' ');
        return `${promoted}${tail ? `<p>${tail}</p>` : ''}`;
      }
    }

    const inline = findEmbeddedSubsectionHeading(plain, sectionRootPrefix);
    if (inline) {
      const num = inline.heading.match(/^(\d+(?:\.\d+)+)/)?.[1];
      const htag = headingTagForSubsectionNumber(num);
      return `${inline.before ? `<p>${inline.before}</p>` : ''}<${htag}>${inline.heading}</${htag}>${inline.after ? `<p>${inline.after}</p>` : ''}`;
    }

    return match;
  });

  // <p><strong>3.1.2 Title</strong></p> or with trailing body in same <p>
  result = result.replace(
    /<(?:p|div)[^>]*>\s*(?:<strong>|<b>)\s*(\d+(?:\.\d+)+)\s+([^<]+?)\s*(?:<\/strong>|<\/b>)\s*([^<]*)<\/(?:p|div)>/gi,
    (match, num, title, rest) => {
      const full = `${num} ${title.trim()}`;
      if (isPartialProtocolHeadingLine(full, sectionRootPrefix)) return match;
      if (!parseProtocolHeadingLine(full, { sectionRootPrefix })) return match;
      const tag = headingTagForSubsectionNumber(num);
      const body = (rest || '').replace(/<[^>]+>/g, '').trim();
      return `<${tag}>${full}</${tag}>${body ? `<p>${body}</p>` : ''}`;
    },
  );

  // <p>3.1 Title</p> (no strong wrapper)
  result = result.replace(
    /<(?:p|div)[^>]*>\s*(\d+(?:\.\d+)+\s+[^<\n]{3,160}?)\s*<\/(?:p|div)>/gi,
    (match, inner) => {
      const text = inner.replace(/<[^>]+>/g, '').trim();
      const promoted = promoteBlock('p', text);
      return promoted || match;
    },
  );

  return result;
}

/**
 * Pre-process section content so subsection lines become detectable headings before HTML conversion.
 */
export function prepareProtocolHeadingContent(content, sectionTitle = '') {
  if (!content || !String(content).trim()) return '';
  const sectionRootPrefix = sectionRootPrefixFromTitle(sectionTitle);
  let s = mergeFragmentedSectionNumbers(String(content).trim());
  s = joinFragmentedHeadingContinuationLines(s, sectionRootPrefix);
  s = normalizeProtocolSubsectionLineBreaks(s);
  const hasHtml = /<\w+[^>]*>/.test(s);
  if (hasHtml) {
    s = joinFragmentedHeadingContinuationInHtml(s, sectionRootPrefix);
    return promoteProtocolSubsectionHeadingsInHtml(s, sectionRootPrefix);
  }
  return promotePlainSubsectionLinesToMarkdown(s, sectionRootPrefix);
}
